<script setup lang="ts">

import { faArrowUpFromBracket, faFilterCircleXmark } from '@fortawesome/pro-regular-svg-icons'

import { FilterTutors } from '@entities/tutor'
import { FilterDateRange } from '@shared/ui/filters'
import { AppInput } from '@shared/ui/inputs'
import { AppMultiSelect, AppSelect } from '@shared/ui/selects'
import { BtnIcon, BtnPrimary } from '@shared/ui/buttons'

import { useFiltersSurveys } from '../model/filters-surveys.hook'

defineOptions({
  name: 'FiltersSurveys',
})

const props = withDefaults(defineProps<{
  /** A discipline's Survey tab: its tutor filter lists the active paid tutors with their paid label, as the legacy tab did. */
  isDisciplinePage?: boolean
  /** The discipline tab passes its context read, so the filters wait for it as the table does. */
  isContextLoading?: boolean
}>(), {
  isDisciplinePage: false,
  isContextLoading: false,
})

const {
  refSearch,
  refDateRange,
  refType,
  refStatuses,
  refSource,
  refTutorIds,
  typeOptions,
  statusOptions,
  sourceOptions,
  canFilterByTutor,
  searchMaxLength,
  isLoading,
  isExporting,
  search,
  clearFilters,
  exportSurveys,
} = useFiltersSurveys({ isContextLoading: () => props.isContextLoading })
</script>

<template>
  <div class="filters-surveys">
    <div class="filters-surveys__fields">
      <AppInput
        v-model="refSearch"
        class="filters-surveys__search"
        label="Search"
        placeholder="Search"
        :max-length="searchMaxLength"
        :disabled="isLoading"
        @keyup:enter="search"
      />

      <FilterDateRange
        v-model="refDateRange"
        presets
        class="filters-surveys__date"
        label="Date"
        placeholder="Search"
        :disabled="isLoading"
      />

      <AppSelect
        v-model="refType"
        class="filters-surveys__select"
        :options="typeOptions"
        label="Survey type"
        placeholder="All"
        :disabled="isLoading"
        clearable
      />

      <AppMultiSelect
        v-model="refStatuses"
        class="filters-surveys__select"
        :options="statusOptions"
        label="Status"
        placeholder="All"
        :disabled="isLoading"
        clearable
      />

      <AppSelect
        v-model="refSource"
        class="filters-surveys__select"
        :options="sourceOptions"
        label="Source"
        placeholder="All"
        :disabled="isLoading"
        clearable
      />

      <FilterTutors
        v-if="canFilterByTutor"
        v-model="refTutorIds"
        class="filters-surveys__tutors"
        :active="isDisciplinePage"
        :paid="isDisciplinePage"
        :with-paid-label="isDisciplinePage"
        :disabled="isLoading"
      />

      <BtnPrimary
        text="Search"
        :disabled="isLoading"
        @click="search"
      />

      <BtnIcon
        :icon="faFilterCircleXmark"
        color="red"
        tooltip-text="Clear filters"
        :disabled="isLoading"
        @click="clearFilters"
      />
    </div>

    <div class="filters-surveys__controls">
      <BtnIcon
        :icon="faArrowUpFromBracket"
        title="Export as CSV"
        tooltip-text="Export as CSV"
        :disabled="isExporting"
        @click="exportSurveys"
      />
    </div>
  </div>
</template>

<style scoped lang="scss" src="./FiltersSurveys.scss" />
